import React from 'react';
import compact from 'lodash.compact';
import IconSections from './sections/iconsSection/IconSections';
import PerformanceLevelSection from './sections/performanceLevelSection/PerformanceLevelSection';
import TrcSection from './sections/trcSection/TrcSection';
import FAQSection from './sections/faqSection/FAQSection';
import AssessmentSummaryCardsSection from './sections/assessmentSummaryCardsSection/AssessmentSummaryCardsSection';
import {
    PROBE_DETAILS_ICONS_GROUP,
    STATIC_ICONS,
    TK,
    TRC_SUMMARY_ICONS_GROUP,
    LICENSE_DEPENDENT,
} from './legendConstants';
import { PERIOD } from '../../../constants/shared/CommonConstants';
import { STUDENT_SUMMARY_PATHS } from '../../../constants/shared/ApiConstants';

const { SS_PROGRESS, SS_TRC_SUMMARY } = STUDENT_SUMMARY_PATHS;

export const getIconsGroup = (optionalIcons = []) => [
    {
        name: 'Icons',
        icons: compact([...STATIC_ICONS, ...optionalIcons]),
    },
];

export const getProbeDetailsGroup = (optionalSubGroups = []) =>
    compact([PROBE_DETAILS_ICONS_GROUP, ...optionalSubGroups]);

export const getLegendPanelTitle = text => (
    <>
        <i className="icon-amplify-help_outline" />
        {text}
    </>
);

export const getStudentSummaryLegendSections = ({
    selectedTabId,
    isTRCSummaryAvailable,
    licensingInfo,
    FAQList,
    trcIcons,
    progressIcons,
    trc,
    hasIconSection = true,
}) => {
    if (selectedTabId === SS_PROGRESS) {
        return compact([
            <PerformanceLevelSection key="performanceLevelSection" />,
            hasIconSection && (
                <IconSections
                    key="progressIconsGroup"
                    sections={progressIcons}
                    sectionClassName="progress-monitoring"
                />
            ),
            <AssessmentSummaryCardsSection key="AssessmentSummaryCardsSection" />,
            <FAQSection key="FAQSection" FAQList={FAQList} />,
        ]);
    }

    const optionalTRCIcons = trcIcons.filter(({ isAvailable }) =>
        isAvailable({ licenses: licensingInfo, trc }),
    );
    const isTRCSummaryPage = selectedTabId === SS_TRC_SUMMARY;
    const trcIconsGroup = getIconsGroup(optionalTRCIcons);

    return compact([
        <PerformanceLevelSection key="performanceLevelSection" />,
        isTRCSummaryPage ? (
            <IconSections key="trcIconsGroup" sections={trcIconsGroup} />
        ) : (
            <AssessmentSummaryCardsSection key="AssessmentSummaryCardsSection" />
        ),
        isTRCSummaryAvailable && <TrcSection />,
        isTRCSummaryPage && (
            <IconSections
                key="trcSummaryIconsGroup"
                sectionClassName="trc-icons"
                sections={TRC_SUMMARY_ICONS_GROUP}
            />
        ),
        <FAQSection key="FAQSection" FAQList={FAQList} />,
    ]);
};

export const isBasicComprehensionSectionVisible = (
    selectedPeriod,
    gradesInCurrentClass,
    gradeToMeasureMapper,
    gradesWithoutBasicComprehension,
) => {
    const gradesWithBasicComprehension = gradeToMeasureMapper
        .filter(grade => !gradesWithoutBasicComprehension.includes(grade.key))
        .map(grade => grade.key);

    return (
        selectedPeriod !== PERIOD.SUMMARY &&
        gradesInCurrentClass.some(grade => gradesWithBasicComprehension.includes(grade))
    );
};

export const getBasicComprehensionIconsGroup = basicComprehensionName => ({
    name: basicComprehensionName,
    icons: [
        {
            iconClassName: 'minus',
            iconText: '-',
            firstTitle: 'No Score',
            secondTitle: 'Entered',
        },
        {
            iconClassName: 'icon-amplify-admin',
            firstTitle: 'Enter/Edit',
            secondTitle: 'Score',
        },
    ],
});

export const buildBenchmarkLegendSections = (
    licensingInfo,
    hasTKInCurrentClass,
    FAQList,
    trc,
    basicComprehensionIconsGroup,
    hasRiskIndicator,
    isSecure,
) => {
    const licenses = hasTKInCurrentClass ? [...licensingInfo, TK] : licensingInfo;
    const optionalIcons = LICENSE_DEPENDENT.filter(({ isAvailable }) =>
        isAvailable({ licenses, trc, hasRiskIndicator, isSecure }),
    );
    const iconsGroup = getIconsGroup(optionalIcons);

    const probeDetailsGroup = getProbeDetailsGroup([basicComprehensionIconsGroup]);

    return compact([
        <PerformanceLevelSection key="performanceLevelSection" />,
        <IconSections key="iconsGroup" sections={iconsGroup} />,
        <IconSections key="probeDetailsGroup" sections={probeDetailsGroup} />,
        licensingInfo.includes(trc) && <TrcSection key="TrcSection" />,
        <FAQSection key="FAQSection" FAQList={FAQList} />,
    ]);
};
